import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LanguageSwitcher } from './components/LanguageSwitcher'
import { CasePanel } from './components/CasePanel'
import { RadarMap } from './components/RadarMap'
import { AddSightingModal } from './components/AddSightingModal'
import { ChipSearchModal } from './components/ChipSearchModal'
import { ReportLostModal } from './components/ReportLostModal'
import { demoLost, demoSightings } from './data/demoLost'

export default function App() {
  const { t } = useTranslation()
  const [panelOpen, setPanelOpen] = useState(true)
  const [selectedAnimalId, setSelectedAnimalId] = useState<number | null>(null)
  const [sightingIndex, setSightingIndex] = useState(0)
  const [modal, setModal] = useState<'sighting' | 'lost' | 'chip' | null>(null)

  const related = useMemo(
    () =>
      selectedAnimalId
        ? demoSightings.filter((s) => s.animal_id === selectedAnimalId)
        : demoSightings,
    [selectedAnimalId],
  )

  function selectAnimal(animalId: number) {
    setSelectedAnimalId(animalId)
    setSightingIndex(0)
    if (window.matchMedia('(max-width: 860px)').matches) {
      setPanelOpen(false)
    }
  }

  return (
    <div className="app-shell">
      <header className="chrome">
        <div className="brand-block">
          <div className="brand">
            Aruba <span>Animal</span> Radar
          </div>
          <div className="brand-sub">{t('tagline')}</div>
        </div>
        <div className="chrome-actions">
          <button
            type="button"
            className="btn btn-panel-toggle"
            onClick={() => setPanelOpen((v) => !v)}
          >
            {panelOpen ? t('panel.closePanel') : t('panel.openPanel')}
          </button>
          <button type="button" className="btn" onClick={() => setModal('chip')}>
            {t('nav.searchChip')}
          </button>
          <button type="button" className="btn" onClick={() => setModal('lost')}>
            {t('nav.reportLost')}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setModal('sighting')}>
            {t('nav.addSighting')}
          </button>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="workspace">
        <section className="map-stage" aria-label={t('nav.map')}>
          <div className="scanline" />
          <RadarMap
            focusAnimalId={selectedAnimalId}
            activeSightingIndex={sightingIndex}
            onMarkerClick={(animalId) => {
              setSelectedAnimalId(animalId)
              const list = demoSightings.filter((s) => s.animal_id === animalId)
              setSightingIndex(0)
              if (list.length) setSightingIndex(0)
            }}
          />
          {selectedAnimalId && related.length > 1 && (
            <div
              style={{
                position: 'absolute',
                zIndex: 5,
                left: '0.75rem',
                bottom: '0.75rem',
                display: 'flex',
                gap: '0.4rem',
                alignItems: 'center',
                background: 'rgba(12,18,25,0.9)',
                border: '1px solid var(--border)',
                padding: '0.45rem 0.55rem',
              }}
            >
              <button
                type="button"
                className="btn"
                onClick={() => setSightingIndex((i) => Math.max(0, i - 1))}
                disabled={sightingIndex <= 0}
              >
                {t('panel.stepPrev')}
              </button>
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.72rem',
                  color: 'var(--cyan)',
                }}
              >
                {t('panel.related')} {sightingIndex + 1}/{related.length} · #
                {related[sightingIndex]?.id}
              </span>
              <button
                type="button"
                className="btn"
                onClick={() => setSightingIndex((i) => Math.min(related.length - 1, i + 1))}
                disabled={sightingIndex >= related.length - 1}
              >
                {t('panel.stepNext')}
              </button>
            </div>
          )}
        </section>

        <CasePanel
          open={panelOpen}
          items={demoLost}
          selectedAnimalId={selectedAnimalId}
          onSelect={selectAnimal}
          onClose={() => setPanelOpen(false)}
        />
      </main>

      <AddSightingModal open={modal === 'sighting'} onClose={() => setModal(null)} />
      <ReportLostModal open={modal === 'lost'} onClose={() => setModal(null)} />
      <ChipSearchModal open={modal === 'chip'} onClose={() => setModal(null)} />
    </div>
  )
}
